import { v4 } from 'uuid';

export type IdFormatHex = 'hex';
export type IdFormatNumber = 'number';
export type IdFormat = IdFormatHex | IdFormatNumber;

export enum IdGeneratorEntityType {
  TranscriptToken = 'TranscriptToken',
  KaraokeConfig = 'KaraokeConfig',
}

const HEX_START = 0x1000; // Start hex IDs from 1000 in hex (4096 decimal)

interface IdRange {
  start: number;
  end: number;
}

interface IdGeneratorConfig {
  format: IdFormat;
  availableRanges: IdRange[];
  maxId: number;
}

/**
 * ID Generator for a specific entity type
 * Each instance manages IDs independently without needing to know the entity type
 */
export class EntityIdGenerator {
  private config: IdGeneratorConfig;
  private readonly format: IdFormat;

  constructor(format: IdFormat = 'number') {
    this.format = format;
    const initialId = this.getInitialId(format);
    this.config = {
      format,
      availableRanges: [],
      maxId: initialId - 1,
    };
  }

  private getInitialId(format: IdFormat): number {
    return format === 'hex' ? HEX_START : 1;
  }

  /**
   * Get next sequential ID
   */
  getNextId(): number | string {
    let nextId: number;

    // Try to reuse an ID from available ranges
    if (this.config.availableRanges.length > 0) {
      const range = this.config.availableRanges[0];
      nextId = range.start;
      range.start++;

      // Remove range if exhausted
      if (range.start > range.end) {
        this.config.availableRanges.shift();
      }
    } else {
      // Generate new ID
      this.config.maxId++;
      nextId = this.config.maxId;
    }

    return this.format === 'hex' ? nextId.toString(16) : nextId;
  }

  /**
   * Get next ID as hex string
   */
  getNextIdHex(): string {
    const nextId = this.getNextId();
    return typeof nextId === 'number' ? nextId.toString(16) : nextId;
  }

  /**
   * Setup or reset ID generator based on a collection of elements with IDs
   */
  setupFromCollection<T extends { id?: string | number }>(
    elements: readonly T[],
  ): void {
    const initialId = this.getInitialId(this.format);

    if (elements.length === 0) {
      this.config = {
        format: this.format,
        availableRanges: [],
        maxId: initialId - 1,
      };
      return;
    }

    // Convert all IDs to numbers and sort
    const usedIds = elements
      .map(element => {
        if (!element.id) return null;
        const numericId =
          typeof element.id === 'number'
            ? element.id
            : parseInt(String(element.id), 16);
        return isNaN(numericId) ? null : numericId;
      })
      .filter((id): id is number => id !== null)
      .sort((a, b) => a - b);

    if (usedIds.length === 0) {
      this.config = {
        format: this.format,
        availableRanges: [],
        maxId: initialId - 1,
      };
      return;
    }

    const maxId = usedIds[usedIds.length - 1];
    const availableRanges: IdRange[] = [];

    // Find gaps in the ID sequence starting from initialId
    let expectedId = initialId;
    for (const usedId of usedIds) {
      if (usedId < initialId) continue; // Skip IDs below the initial ID

      if (usedId > expectedId) {
        // Found a gap
        availableRanges.push({ start: expectedId, end: usedId - 1 });
      }
      expectedId = Math.max(expectedId, usedId + 1);
    }

    this.config = {
      format: this.format,
      availableRanges,
      maxId,
    };
  }

  /**
   * Reset ID generator to initial state
   */
  reset(): void {
    const initialId = this.getInitialId(this.format);
    this.config = {
      format: this.format,
      availableRanges: [],
      maxId: initialId - 1,
    };
  }

  /**
   * Get current max ID
   */
  getMaxId(): number {
    return this.config.maxId;
  }

  /**
   * Get available ranges count
   */
  getAvailableRangesCount(): number {
    return this.config.availableRanges.length;
  }
}

/**
 * Map-like structure that provides EntityIdGenerator instances per entity type
 * Automatically creates new generators when accessing non-existent entity types
 */
class IdGeneratorMap {
  private generators: Map<string, EntityIdGenerator> = new Map();
  private defaultFormat: IdFormat = 'number';

  /**
   * Get or create an ID generator for a specific entity type
   * @param entityType - The entity type (e.g., 'TranscriptToken', 'KaraokeConfig')
   * @param format - ID format (only used when creating new generator)
   * @returns EntityIdGenerator instance for the entity type
   */
  get(
    entityType: string,
    format: IdFormat = this.defaultFormat,
  ): EntityIdGenerator {
    if (!this.generators.has(entityType)) {
      this.generators.set(entityType, new EntityIdGenerator(format));
    }
    return this.generators.get(entityType)!;
  }

  /**
   * Check if a generator exists for the entity type
   */
  has(entityType: string): boolean {
    return this.generators.has(entityType);
  }

  /**
   * Delete a generator for a specific entity type
   */
  delete(entityType: string): boolean {
    return this.generators.delete(entityType);
  }

  /**
   * Clear all generators
   */
  clear(): void {
    this.generators.clear();
  }

  /**
   * Get all entity types that have generators
   */
  keys(): IterableIterator<string> {
    return this.generators.keys();
  }

  /**
   * Get all generators
   */
  values(): IterableIterator<EntityIdGenerator> {
    return this.generators.values();
  }

  /**
   * Get all entity type and generator pairs
   */
  entries(): IterableIterator<[string, EntityIdGenerator]> {
    return this.generators.entries();
  }

  /**
   * Get number of entity types with generators
   */
  get size(): number {
    return this.generators.size;
  }

  /**
   * Set default format for new generators
   */
  setDefaultFormat(format: IdFormat): void {
    this.defaultFormat = format;
  }

  /**
   * Generate a short random ID (not entity-specific)
   */
  shortRandomId(): string {
    return v4().substring(0, 8);
  }
}

/**
 * Global ID generator map
 * Usage:
 *   const tokenGenerator = IdGenerator.get('TranscriptToken', 'hex');
 *   const nextId = tokenGenerator.getNextId();
 *
 *   const configGenerator = IdGenerator.get('KaraokeConfig');
 *   const nextConfigId = configGenerator.getNextId();
 */
export const IdGenerator = new IdGeneratorMap();
